import { useEffect, useMemo, useState } from 'react';
import { SharedIcon } from 'components/SharedIcon/SharedIcon';
import { ResourceRow } from './ResourceTabCard';
import { StylishNewButton } from 'components/DesignSystems/New/StylishNewButton';
import { FormProvider, useForm, useWatch } from 'react-hook-form';
import FormTextField from 'components/IAP/formFields/FormTextField';
import FormSelectField from 'components/IAP/formFields/FormSelectField';
import { useAllIncidents } from 'components/IAP/hooks/incidentHooks';
import { GroupRosterItem } from 'hooks/incident/useRoster';
import FormDateTimeField from 'components/IAP/formFields/FormDateTimeField';
import FormTextAreaField from 'components/IAP/formFields/FormTextAreaField';
import { useAors } from 'hooks/useAors';
import ResourceSelectFields from 'components/IAP/ResourceSelectFields';
import {
  EditResourceSetFormData,
  type ResourceSet,
  useResourceSets,
  useUpdateResourceSet,
} from 'components/IAP/hooks/resourceSetHooks';
import { ASSET_STATUS_FILTER_OPTIONS } from './resourceFilterOptions';
import { type UscgSymbol } from '../LayersTab/utils/uscgSymbology';
import { useUscgMapContext } from '../LayersTab/UscgMapContext';
import FormSymbologySelect from '../LayersTab/FormSymbologySelect';
import { usePrimaryPoint } from '../LayersTab/utils/usePrimaryPoint';
import { NULL_VALUE } from 'constants/displayConstants';
import { useGetLatestIncidentStatusForUser } from 'hooks/incident/useWorkLog';
import classNames from 'classnames';

type ViewResourceCardProps = {
  row: ResourceRow;
  setIsEditing: (isEditing: boolean) => void;
  groupMembers: GroupRosterItem[];
  compact?: boolean;
};

type EditResourceFormValues = {
  id: string;
  name: string;
  kind: string;
  type: string;
  assetStatus: string;
  status: string;
  incidentId: string;
  owningOrganization: string;
  assigneeIds: string[];
  capabilities: string;
  address: string;
  dateTimeOrdered: string;
  pointOfContact: string;
  quantity: number | string;
  unit: string;
  personsOnBoard: string;
  hullTailNumber: string;
  aor: string;
  opcon: string;
  tacon: string;
  symbology: string;
  latitude: string;
  longitude: string;
  persons_on_board: string;
  hull_tail_number: string;
  datetimeOrderedOffset: number;
  eta: string;
  etaTimezoneOffset: number;
  notes: string;
};

const mapResourceSetToEditPayload = (
  resourceSetId: string,
  existingResourceSet?: ResourceSet
): Partial<EditResourceSetFormData> => {
  if (!existingResourceSet) {
    return {};
  }

  return {
    id: existingResourceSet.id,
    resourceSetId,
    resourceSiteId: existingResourceSet.resource_site_id,
    groupGuid: existingResourceSet.group_guid,
    name: existingResourceSet.name,
    description: existingResourceSet.description,
    geolocation: existingResourceSet.geolocation,
    kind: existingResourceSet.kind,
    type: existingResourceSet.type,
    assetStatus: existingResourceSet.asset_status,
    quantity: existingResourceSet.quantity,
    status: existingResourceSet.status,
    consolidationTargetId: existingResourceSet.consolidation_target_id,
    archived: existingResourceSet.archived,
    dateTimeOrdered: existingResourceSet.date_time_ordered,
    datetimeOrderedOffset: existingResourceSet.datetime_ordered_offset,
    eta: existingResourceSet.eta,
    incidents: existingResourceSet.incidents,
    sourceLocation: existingResourceSet.source_location,
    sourceResourceSiteId: existingResourceSet.source_resource_site_id,
    destinationLocation: existingResourceSet.destination_location,
    destinationResourceSiteId: existingResourceSet.destination_resource_site_id,
  };
};
const EditResourceCard = ({
  row,
  setIsEditing,
  groupMembers,
  compact,
}: ViewResourceCardProps) => {
  const isPersonnel = row.kind === 'Personnel';
  const { startDrawing, stopDrawing } = useUscgMapContext();
  const [dateTimeOrderedTimezone, setDateTimeOrderedTimezone] = useState(
    row.datetimeOrderedOffset ?? 0
  );
  const { mutate: fetchStatuses, data: personnelStatusData } =
    useGetLatestIncidentStatusForUser();
  const [etaTimezone, setEtaTimezone] = useState(row.etaTimezoneOffset ?? 0);
  const { mutate: updateResourceSet } = useUpdateResourceSet(
    row.resource_set_id || ''
  );
  const { data: resourceSetsData } = useResourceSets();
  const { data: incidents = [] } = useAllIncidents();

  const normalizedResourceSetsData = resourceSetsData as
    | ResourceSet[]
    | { rows: ResourceSet[] }
    | undefined;
  const resourceSets = Array.isArray(normalizedResourceSetsData)
    ? normalizedResourceSetsData
    : (normalizedResourceSetsData?.rows ?? []);

  const incidentName = row.incident?.name || row.incident_details?.[0]?.name;
  const defaultIncidentId = useMemo(() => {
    return (
      row.incident?.id ||
      row.incident_details?.[0]?.id ||
      incidents.find((incident) => incident.name === incidentName)?.id ||
      ''
    );
  }, [row.incident, row.incident_details, incidentName, incidents]);

  const formMethods = useForm<EditResourceFormValues>({
    mode: 'onChange',
    defaultValues: {
      id: row.id,
      name: row.name,
      kind: row.kind,
      type: row.type || '',
      assetStatus: row.assetStatus || '',
      status: row.status || '',
      incidentId: defaultIncidentId,
      quantity: row.quantity ?? '',
      owningOrganization: row.owning_organization || '',
      assigneeIds: row?.assignees?.map((user) => user.userGuid) || [],
      capabilities: row.description || '',
      address: row.address || '',
      dateTimeOrdered: row.datetimeOrdered || '',
      pointOfContact: row.pointOfContact?.userGuid || '',
      unit: row.unit || '',
      personsOnBoard: row.persons_on_board || '',
      hullTailNumber: row.hull_tail_number || '',
      aor: row.aor,
      opcon: row.opcon,
      tacon: row.tacon,
      symbology: row.symbology || '',
      latitude: row.latitude != null ? String(row.latitude) : '',
      longitude: row.longitude != null ? String(row.longitude) : '',
      eta: row.eta || '',
      etaTimezoneOffset: row.etaTimezoneOffset ?? 0,
      notes: row.notes || '',
    },
  });

  const { handleSubmit, control } = formMethods;

  const selectedKind = useWatch({ control, name: 'kind' });
  const isStatusEditable =
    row.status === 'Not Arrived' ||
    row.status === 'In Transit' ||
    row.status === 'On Site';

  const currentResourceSet = resourceSets.find(
    (resourceSet: ResourceSet) =>
      resourceSet.resource_set_id === row.resource_set_id
  );

  const onSubmit = (data: EditResourceFormValues) => {
    const payload = {
      name: data.name,
      kind: data.kind,
      type: data.type,
      assetStatus: data.assetStatus,
      status: data.status,
      incidentId: data.incidentId,
      owningOrganization: data.owningOrganization,
      assigneeIds: data.assigneeIds,
      dateTimeOrdered: data.dateTimeOrdered,
      datetimeOrderedOffset: dateTimeOrderedTimezone,
      pointOfContact: data.pointOfContact,
      address: data.address,
      capabilities: data.capabilities,
      quantity: data.quantity !== '' ? Number(data.quantity) : undefined,
      unit: data.unit,
      personsOnBoard: data.personsOnBoard,
      hullTailNumber: data.hullTailNumber,
      symbology: data.symbology || undefined,
      geometry: (() => {
        const lat = Number(data.latitude);
        const lng = Number(data.longitude);
        if (data.latitude && data.longitude && !isNaN(lat) && !isNaN(lng)) {
          return {
            type: 'Point' as const,
            coordinates: [lng, lat] as [number, number],
          };
        }
        return undefined;
      })(),
      aor: data.aor || undefined,
      opcon: data.opcon || undefined,
      tacon: data.tacon || undefined,
      eta: data.eta || undefined,
      etaTimezoneOffset: etaTimezone,
      notes: data.notes || undefined,
    };

    const existingResourceSetPayload = mapResourceSetToEditPayload(
      row.resource_set_id || '',
      currentResourceSet
    );

    updateResourceSet({
      ...existingResourceSetPayload,
      ...payload,
      description: data.capabilities,
      incidents: data.incidentId
        ? [data.incidentId]
        : existingResourceSetPayload.incidents,
    });
    setIsEditing(false);
  };

  const { data: aors = [] } = useAors();

  const primaryPoint = usePrimaryPoint();

  useEffect(() => {
    if (primaryPoint) {
      formMethods.setValue('latitude', String(primaryPoint.latitude));
      formMethods.setValue('longitude', String(primaryPoint.longitude));
    }
  }, [primaryPoint, formMethods]);

  useEffect(() => {
    if (
      row.assignees &&
      row.assignees?.length > 0 &&
      !(row.assignees.length === 1 && !row.assignees[0]) &&
      defaultIncidentId &&
      row.kind === 'Personnel'
    ) {
      fetchStatuses({
        users: row.assignees || [],
        incidentId: defaultIncidentId || '',
      });
    }
  }, [row.assignees, defaultIncidentId, row.kind, fetchStatuses]);

  useEffect(() => {
    return () => {
      stopDrawing();
    };
  }, [stopDrawing]);

  const handleSymbologyChange = (type: string | null) => {
    if (type) {
      startDrawing(type as UscgSymbol);
    } else {
      stopDrawing();
    }
    formMethods.setValue('latitude', '');
    formMethods.setValue('longitude', '');
  };

  const handleCancel = () => {
    stopDrawing();
    setIsEditing(false);
  };

  if (compact) {
    return (
      <FormProvider {...formMethods}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="resource-card-form-header">
            <span className="fw-semibold">Edit &ldquo;{row.name}&rdquo;</span>
            <StylishNewButton
              type="button"
              className="button--icon"
              aria-label="Close edit resource form"
              onClick={(e) => {
                e.stopPropagation();
                handleCancel();
              }}
            >
              <SharedIcon iconName="cancel" size="sm" />
            </StylishNewButton>
          </div>
          <div className="resource-card-content d-flex flex-column p-4">
            <FormTextField
              title="Resource Name"
              fieldName="name"
              rules={{
                required: {
                  value: true,
                  message: 'Resource Name is required',
                },
              }}
              disabled={row.isTemplate}
            />
            <ResourceSelectFields labels disableKind={'Personnel'} />
            <div className="d-flex gap-2">
              <div className="w-50">
                <FormSelectField
                  title="AOR"
                  fieldName="aor"
                  options={aors.map((aor) => ({
                    value: aor.id,
                    label: aor.name,
                  }))}
                  disabled={isPersonnel || selectedKind === 'Personnel'}
                />
              </div>
              <div className="w-50">
                <FormSelectField
                  title="Incident"
                  fieldName="incidentId"
                  options={incidents.map((incident) => ({
                    value: incident.id,
                    label: incident.name,
                  }))}
                  rules={{
                    ...(selectedKind === 'Personnel'
                      ? {
                          required: {
                            value: true,
                            message: 'Incident is required',
                          },
                        }
                      : {}),
                  }}
                  disabled={isPersonnel}
                />
              </div>
            </div>
            <div className="d-flex gap-2">
              <div className="w-50">
                <FormSelectField
                  title="OPCON"
                  fieldName="opcon"
                  options={aors.map((aor) => ({
                    value: aor.id,
                    label: aor.name,
                  }))}
                  isClearable
                />
              </div>
              <div className="w-50">
                <FormSelectField
                  title="TACON"
                  fieldName="tacon"
                  options={aors.map((aor) => ({
                    value: aor.id,
                    label: aor.name,
                  }))}
                  isClearable
                />
              </div>
            </div>
            <div className="d-flex gap-2">
              <div className="w-50">
                <FormSelectField
                  title="Asset Status"
                  fieldName="assetStatus"
                  options={ASSET_STATUS_FILTER_OPTIONS.map((status) => ({
                    value: status,
                    label: status,
                  }))}
                />
              </div>
              <div className="w-50">
                {isPersonnel ? (
                  <div className="d-flex flex-column">
                    {personnelStatusData?.map((person) => (
                      <div
                        key={person.userName}
                        className="d-flex flex-column align-items-start mt-1"
                      >
                        <div className="col-3">{person.userName}: </div>
                        <div
                          className={classNames(
                            'resource-checkin-status-badge col-2 me-2',
                            {
                              'resource-checkin-status-badge--pending-activation':
                                person.status === 'Pending Activation',
                              'resource-checkin-status-badge--declined':
                                person.status === 'Declined',
                              'resource-checkin-status-badge--activated':
                                person.status === 'Activated',
                              'resource-checkin-status-badge--checked-in':
                                person.status === 'Checked In',
                              'resource-checkin-status-badge--signed-in':
                                person.status === 'Signed In',
                            }
                          )}
                        >
                          {person.status ?? NULL_VALUE}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <FormSelectField
                    title="Check-in Status"
                    fieldName="status"
                    options={[
                      { value: 'Not Arrived', label: 'Not Arrived' },
                      { value: 'In Transit', label: 'In Transit' },
                      { value: 'On Site', label: 'On Site' },
                    ]}
                    disabled={!isStatusEditable}
                  />
                )}
              </div>
            </div>
            <div className="d-flex gap-2">
              <div className="w-50">
                <FormTextField title="Current Location" fieldName="address" />
              </div>
            </div>
            <div className="d-flex gap-2">
              <div className="w-50">
                <FormDateTimeField
                  title="Datetime Ordered"
                  fieldName="dateTimeOrdered"
                />
              </div>
              <div className="w-50">
                <FormSelectField
                  title="Point of Contact"
                  fieldName="pointOfContact"
                  options={groupMembers.map((member) => ({
                    label: member.email_address,
                    value: member.user_guid,
                  }))}
                  isClearable={true}
                />
              </div>
            </div>
            {isPersonnel && (
              <div>
                <div>Assignees</div>
                {Array.isArray(row?.assignees) && row.assignees.length > 0 ? (
                  row.assignees.length === 1 && !row.assignees[0] ? (
                    <div>{NULL_VALUE}</div>
                  ) : (
                    row.assignees.map((assignee) => (
                      <div key={assignee.userGuid}>
                        {assignee.userName || assignee.emailAddress}
                      </div>
                    ))
                  )
                ) : (
                  <div>{NULL_VALUE}</div>
                )}
              </div>
            )}
            <div className="d-flex gap-2">
              <div className="w-50">
                <FormTextField
                  title="Owning Organization"
                  fieldName="owningOrganization"
                />
              </div>
              <div className="w-50">
                <FormTextField
                  title="Hull/Tail Number"
                  fieldName="hullTailNumber"
                />
              </div>
            </div>
            <div className="d-flex gap-2">
              <div className="w-50">
                <FormTextField title="Quantity" fieldName="quantity" />
              </div>
              <div className="w-50">
                <FormDateTimeField
                  title="Estimated Time of Arrival"
                  fieldName="eta"
                  timeZone
                  timeZoneLabel
                  timeZoneValue={etaTimezone}
                  onTimeZoneChange={(option) => setEtaTimezone(option.value)}
                />
              </div>
            </div>
            <div className="d-flex gap-2">
              <div className="w-50">
                <FormTextField title="Unit" fieldName="unit" />
              </div>
              <div className="w-50">
                <FormTextField title="Capabilities" fieldName="capabilities" />
              </div>
            </div>
            <div className="d-flex align-items-start gap-2">
              <div className="col-4">
                <FormSymbologySelect
                  excludeOptions={['completedBoom', 'proposedBoom', 'oilSpill']}
                  onSymbologyChange={handleSymbologyChange}
                />
              </div>
              <div className="col-2">
                <FormTextField title="Latitude" fieldName="latitude" />
              </div>
              <div className="col-2">
                <FormTextField title="Longitude" fieldName="longitude" />
              </div>
            </div>
            <FormTextAreaField title="Notes" fieldName="notes" />
            <div className="d-flex gap-2">
              <StylishNewButton type="submit">Save</StylishNewButton>
              <StylishNewButton
                type="button"
                className="button--secondary"
                onClick={handleCancel}
              >
                Cancel
              </StylishNewButton>
            </div>
          </div>
        </form>
      </FormProvider>
    );
  }

  return (
    <FormProvider {...formMethods}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="resource-card-form-header">
          <span className="fw-semibold">Edit &ldquo;{row.name}&rdquo;</span>
          <StylishNewButton
            type="button"
            className="button--icon"
            aria-label="Close edit resource form"
            onClick={(e) => {
              e.stopPropagation();
              handleCancel();
            }}
          >
            <SharedIcon iconName="cancel" size="sm" />
          </StylishNewButton>
        </div>
        <div className="resource-card-content p-4">
          <div className="resource-card-section row gx-1 gy-2">
            <div className="col-xl-4 col-lg-6">
              <FormTextField
                title="Resource Name"
                fieldName="name"
                rules={{
                  required: {
                    value: true,
                    message: 'Resource Name is required',
                  },
                }}
                disabled={row.isTemplate}
              />
            </div>
            <div className="col-xl-4 col-lg-6 mt-3">
              <ResourceSelectFields
                labels
                disableKind={isPersonnel ? undefined : 'Personnel'}
                disabled={isPersonnel}
              />
            </div>
            <div className="col-xl-2 col-md-6">
              <FormSelectField
                title="AOR"
                fieldName="aor"
                options={aors.map((aor) => ({
                  value: aor.id,
                  label: aor.name,
                }))}
                disabled={isPersonnel || selectedKind === 'Personnel'}
              />
            </div>
            <div className="col-xl-2 col-md-6">
              <FormSelectField
                title="Incident"
                fieldName="incidentId"
                options={incidents.map((incident) => ({
                  value: incident.id,
                  label: incident.name,
                }))}
                rules={{
                  ...(selectedKind === 'Personnel'
                    ? {
                        required: {
                          value: true,
                          message: 'Incident is required',
                        },
                      }
                    : {}),
                }}
                disabled={isPersonnel}
              />
            </div>
          </div>
          <div className="resource-card-section row gx-1 gy-2">
            <div className="col-xl-2 col-md-6">
              <FormSelectField
                title="OPCON"
                fieldName="opcon"
                options={aors.map((aor) => ({
                  value: aor.id,
                  label: aor.name,
                }))}
                isClearable
              />
            </div>
            <div className="col-xl-2 col-md-6">
              <FormSelectField
                title="TACON"
                fieldName="tacon"
                options={aors.map((aor) => ({
                  value: aor.id,
                  label: aor.name,
                }))}
                isClearable
              />
            </div>
            <div className="col-xl-2 col-md-6">
              <FormSelectField
                title="Asset Status"
                fieldName="assetStatus"
                options={ASSET_STATUS_FILTER_OPTIONS.map((status) => ({
                  value: status,
                  label: status,
                }))}
              />
            </div>
            <div className="col-xl-2 col-md-6">
              {isPersonnel ? (
                <div className="d-flex flex-column">
                  Check-in Status
                  {personnelStatusData?.map((person) => (
                    <div
                      key={person.userName}
                      className="d-flex flex-column align-items-start mt-1"
                    >
                      <div className="col-3">{person.userName}: </div>
                      <div
                        className={classNames(
                          'resource-checkin-status-badge me-2',
                          {
                            'resource-checkin-status-badge--pending-activation':
                              person.status === 'Pending Activation',
                            'resource-checkin-status-badge--declined':
                              person.status === 'Declined',
                            'resource-checkin-status-badge--activated':
                              person.status === 'Activated',
                            'resource-checkin-status-badge--checked-in':
                              person.status === 'Checked In',
                            'resource-checkin-status-badge--signed-in':
                              person.status === 'Signed In',
                          }
                        )}
                      >
                        {person.status ?? NULL_VALUE}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <FormSelectField
                  title="Check-in Status"
                  fieldName="status"
                  options={[
                    { value: 'Not Arrived', label: 'Not Arrived' },
                    { value: 'In Transit', label: 'In Transit' },
                    { value: 'On Site', label: 'On Site' },
                  ]}
                  disabled={!isStatusEditable || isPersonnel}
                />
              )}
            </div>
            <div className="col-xl-2 col-md-6">
              <FormTextField title="Current Location" fieldName="address" />
            </div>
            <div className="col-xl-2 col-md-6">
              <FormDateTimeField
                title="Datetime Ordered"
                fieldName="dateTimeOrdered"
                timeZone
                timeZoneLabel
                timeZoneValue={dateTimeOrderedTimezone}
                onTimeZoneChange={(option) =>
                  setDateTimeOrderedTimezone(option.value)
                }
              />
            </div>
          </div>
          {isPersonnel && (
            <div className="resource-card-section row gx-1 gy-2">
              <div className="col-xl-4 col-lg-6">
                <div>Assignees</div>
                {Array.isArray(row?.assignees) && row.assignees.length > 0 ? (
                  row.assignees.length === 1 && !row.assignees[0] ? (
                    <div>{NULL_VALUE}</div>
                  ) : (
                    row.assignees.map((assignee) => (
                      <div key={assignee.userGuid}>
                        {assignee.userName || assignee.emailAddress}
                      </div>
                    ))
                  )
                ) : (
                  <div>{NULL_VALUE}</div>
                )}
              </div>
            </div>
          )}
          <div className="resource-card-section row gx-1 gy-2">
            <div className="col-xl-3 col-md-6">
              <FormSelectField
                title="Point of Contact"
                fieldName="pointOfContact"
                options={groupMembers.map((member) => ({
                  label: member.email_address,
                  value: member.user_guid,
                }))}
                isClearable={true}
              />
            </div>
            <div className="col-xl-3 col-md-6">
              <FormTextField
                title="Owning Organization"
                fieldName="owningOrganization"
              />
            </div>
            <div className="col-xl-2 col-md-6">
              <FormTextField
                title="Hull/Tail Number"
                fieldName="hullTailNumber"
              />
            </div>
            <div className="col-xl-2 col-md-6">
              <FormTextField title="Quantity" fieldName="quantity" />
            </div>
            <div className="col-xl-2 col-md-6">
              <FormTextField title="Unit" fieldName="unit" />
            </div>
          </div>
          <div className="resource-card-section row gx-1 gy-2 align-items-start">
            <div className="col-xl-3 col-md-6">
              <FormDateTimeField
                title="Estimated Time of Arrival"
                fieldName="eta"
                timeZone
                timeZoneLabel
                timeZoneValue={etaTimezone}
                onTimeZoneChange={(option) => setEtaTimezone(option.value)}
              />
            </div>
            <div className="col-xl-3 col-md-6">
              <FormTextField title="Capabilities" fieldName="capabilities" />
            </div>
            <div className="col-xl-2 col-lg-3">
              <FormSymbologySelect
                excludeOptions={['completedBoom', 'proposedBoom', 'oilSpill']}
                onSymbologyChange={handleSymbologyChange}
              />
            </div>
            <div className="col-xl-2 col-md-3">
              <FormTextField title="Latitude" fieldName="latitude" />
            </div>
            <div className="col-xl-2 col-md-3">
              <FormTextField title="Longitude" fieldName="longitude" />
            </div>
          </div>
          <div className="resource-card-section row gx-1 gy-2">
            <div className="col-12">
              <FormTextAreaField title="Notes" fieldName="notes" />
            </div>
          </div>
          <div className="d-flex mt-4 gap-2">
            <StylishNewButton type="submit">Save</StylishNewButton>
            <StylishNewButton
              type="button"
              className="button--secondary"
              onClick={handleCancel}
            >
              Cancel
            </StylishNewButton>
          </div>
        </div>
      </form>
    </FormProvider>
  );
};

export default EditResourceCard;
